/**
 * Lesson Detail Screen (INT-SCR-006)
 * Displays lesson content with v3 bilingual sections, all 9 exercise types,
 * progress tracking, AI exercise generation, voice gating, and completion flow.
 */

import React, { useEffect, useCallback, useMemo } from 'react';
import { ScrollView, TouchableOpacity, ActivityIndicator, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';

import { ErrorState, LoadingState } from '../components/ui';
import { ScreenHeader } from '../components/ui/ScreenHeader';
import { WebContainer } from '../components/ui/WebContainer';
import {
  Box,
  VStack,
  HStack,
  Text,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { colors, FS_XS, FS_SM, FS_MD, FS_LG } from '../theme';
import { useLessonStore } from '../store/lessonStore';
import {
  selectCurrentLesson,
  selectIsLoadingLesson,
  selectExerciseProgress,
  selectIsSubmittingCompletion,
  selectFetchLesson,
  selectUpdateExerciseProgress,
  selectSubmitCompletion,
  selectResetLessonState,
  selectLessonError,
  selectLastCompletionResult,
  selectCompletionError,
  selectDismissCompletion,
  selectRetryLesson,
} from '../store/lessonSelectors';
import {
  selectLessonLanguage,
  selectSourceLocale,
  selectTargetLocale,
  selectVoiceExercisesEnabled,
  selectLessonFeedbackEnabled,
} from '../store/userPreferencesSelectors';
import { useUserPreferencesStore } from '../store/userPreferencesStore';
import { useAudioLessonsStore } from '../store/audioLessonsStore';
import { selectResolveLinkedEpisode, selectLinkedEpisodeSlug } from '../store/audioLessonsSelectors';
import { BilingualText, LessonProgressBar, AIExerciseLoader, FeedbackButton, LessonScoreCard } from '../components/lessons';
import { ReorderWordsExercise, MatchPairsExercise, RepeatAfterExercise, SpeakExercise, PronunciationChallengeExercise, ListenAndChooseExercise, FillOrChooseExercise, DictationExercise, ErrorCorrectionExercise, DialogueCompletionExercise, FreeWriteExercise, ListenAndRespondExercise, ExerciseErrorBoundary } from '../components/exercises';
import { resolveBilingualContent, getActiveLocale, isBilingualContent } from '../types/lesson';
import { SPEECH_RECOGNITION_AVAILABLE } from '../config/speechCapabilities';
import { AUDIO_LESSONS_ENABLED } from '../config/constants';
import type { LessonLanguage, SourceLocale, TargetLocale } from '../types/locale';
import type {
  LessonContent,
  LessonSection,
  LessonExerciseContent,
  GeneratedExercise,
  ExerciseProgress,
  ExerciseType,
} from '../types/lesson';
import { useDisplayTheme } from '../hooks/useDisplayTheme';

interface LessonDetailScreenProps {
  route?: {
    params: {
      slug?: string;
      title?: string;
      draftContent?: LessonContent;
      draftTitle?: string;
    };
  };
  /** Direct slug prop for embedded/split-view usage */
  slug?: string;
  /** Whether shown as an embedded panel (hides SafeAreaView chrome) */
  embedded?: boolean;
  /** Callback when a lesson link is tapped in embedded mode */
  onNavigateToLesson?: (slug: string) => void;
}

export function LessonDetailScreen({ route, slug: directSlug, embedded, onNavigateToLesson }: LessonDetailScreenProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigation: any = useNavigation();
  const { slug: routeSlug, title: routeTitle, draftContent, draftTitle } = route?.params ?? {};
  const slug = directSlug ?? routeSlug;
  const { bottom: bottomInset } = useSafeAreaInsets();
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  // Lesson store
  const currentLesson = useLessonStore(selectCurrentLesson);
  const isLoadingLesson = useLessonStore(selectIsLoadingLesson);
  const exerciseProgress = useLessonStore(selectExerciseProgress);
  const isSubmittingCompletion = useLessonStore(selectIsSubmittingCompletion);
  const fetchLesson = useLessonStore(selectFetchLesson);
  const updateExerciseProgress = useLessonStore(selectUpdateExerciseProgress);
  const submitCompletion = useLessonStore(selectSubmitCompletion);
  const resetLessonState = useLessonStore(selectResetLessonState);
  const error = useLessonStore(selectLessonError);
  const completionResult = useLessonStore(selectLastCompletionResult);
  const completionError = useLessonStore(selectCompletionError);
  const dismissCompletion = useLessonStore(selectDismissCompletion);
  const retryLesson = useLessonStore(selectRetryLesson);

  // User preferences for bilingual content
  const lessonLanguage = useUserPreferencesStore(selectLessonLanguage);
  const sourceLocale = useUserPreferencesStore(selectSourceLocale);
  const targetLocale = useUserPreferencesStore(selectTargetLocale);
  // Voice exercises need the native recognizer relay (server-speech-proxy Phase 2);
  // on web the relay is unavailable, so exercises fall back to their typed/scoring branch.
  const voiceExercisesEnabled =
    useUserPreferencesStore(selectVoiceExercisesEnabled) && SPEECH_RECOGNITION_AVAILABLE;
  const lessonFeedbackEnabled = useUserPreferencesStore(selectLessonFeedbackEnabled);

  // For draft content, skip fetching
  const isDraft = !!draftContent;
  const lesson = isDraft ? null : currentLesson;
  const content = draftContent || (lesson?.content as LessonContent | undefined);
  const title = draftTitle || lesson?.title || routeTitle || ' ';

  // Count total exercises for progress tracking
  const totalExercises = useMemo(() => {
    if (!content?.sections) return 0;
    return content.sections.reduce((count: number, section: LessonSection) => {
      return count + (section.exercises?.length || 0);
    }, 0);
  }, [content]);

  const completedExercises = Object.keys(exerciseProgress).length;

  // Fetch lesson on mount
  useEffect(() => {
    if (!isDraft && slug) {
      fetchLesson(slug);
    }
    return () => {
      if (!isDraft) resetLessonState();
    };
  }, [slug, isDraft, fetchLesson, resetLessonState]);

  // Resolve the lesson's linked audio episode (for "Listen on the go"). The
  // resolved slug is read reactively from the store cache; the effect only
  // triggers the (cached) async lookup. Fails closed to null so the button
  // stays hidden on any lookup error (AUDIO-LESSONS-009). Skipped entirely
  // while Audio Lessons is shelved (ADR-032) — no lookup request, no button.
  const resolveLinkedEpisode = useAudioLessonsStore(selectResolveLinkedEpisode);
  const linkedEpisodeSlug = useAudioLessonsStore(selectLinkedEpisodeSlug(slug));
  useEffect(() => {
    if (!isDraft && slug && AUDIO_LESSONS_ENABLED) void resolveLinkedEpisode(slug);
  }, [isDraft, slug, resolveLinkedEpisode]);

  // Handle exercise completion
  const handleExerciseComplete = useCallback(
    (exerciseId: string, type: string, correct: boolean, score: number, extras?: Partial<ExerciseProgress>) => {
      updateExerciseProgress(exerciseId, {
        completed: true,
        correct,
        score,
        type: type as ExerciseType,
        voiceUsed: extras?.voiceUsed || false,
        userAnswer: extras?.userAnswer,
        userOrder: extras?.userOrder,
        matchedPairs: extras?.matchedPairs,
      });
    },
    [updateExerciseProgress]
  );

  // Handle lesson completion — submit, then show the score card.
  //
  // Navigation is deliberately NOT fired here (LSC-001). It used to run
  // straight after the await, which meant the review schedule the server
  // had just computed was never shown to anyone. Leaving is now the
  // learner's call, via Continue.
  const handleComplete = useCallback(async () => {
    if (!slug) return;
    try {
      await submitCompletion(slug);
    } catch {
      // Surfaced via completionError below
    }
  }, [slug, submitCompletion]);

  // Score card — Continue leaves (LSC-002), Try Again re-attempts in place (LSC-003)
  const handleContinue = useCallback(() => {
    dismissCompletion();
    navigation?.goBack?.();
  }, [dismissCompletion, navigation]);

  const handleRetryLesson = useCallback(() => {
    retryLesson();
  }, [retryLesson]);

  // Handle retry
  const handleRetry = useCallback(() => {
    if (slug) {
      fetchLesson(slug);
    }
  }, [slug, fetchLesson]);

  // Loading state
  if (isLoadingLesson && !isDraft) {
    return (
      <View style={{ flex: 1, backgroundColor: theme.background, justifyContent: 'center' }}>
        <LoadingState testID="lesson-loading" />
      </View>
    );
  }

  // Error or no-content state
  if (error || !content) {
    return (
      <View style={{ flex: 1, backgroundColor: theme.background, justifyContent: 'center' }}>
        <ErrorState
          testID="lesson-load-error"
          message={error ? t('lessons.detail.unableToLoad') : t('lessons.detail.noContent')}
          retryLabel={t('common.retry')}
          onRetry={handleRetry}
        />
      </View>
    );
  }

  // Completion succeeded — the score card replaces the lesson body until the
  // learner dismisses it (LSC-001). This is the only surface that shows the
  // spaced-repetition schedule the completion just produced.
  if (completionResult) {
    return (
      <View testID="lesson-detail-screen" style={{ flex: 1, backgroundColor: theme.background }}>
        {!embedded && <ScreenHeader title={title} />}
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ padding: 16, paddingBottom: 8 + bottomInset }}
          showsVerticalScrollIndicator
        >
          <WebContainer variant="content">
            <LessonScoreCard
              result={completionResult}
              onContinue={handleContinue}
              onRetry={handleRetryLesson}
            />
          </WebContainer>
        </ScrollView>
      </View>
    );
  }

  return (
    <View testID="lesson-detail-screen" style={{ flex: 1, backgroundColor: theme.background }}>
      {!embedded && <ScreenHeader title={title} />}

      {/* Progress bar */}
      {totalExercises > 0 && (
        <LessonProgressBar
          current={completedExercises}
          total={totalExercises}
          label={t('lessons.detail.exercises')}
        />
      )}

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ padding: 16, paddingBottom: 8 + bottomInset }}
        showsVerticalScrollIndicator
      >
        <WebContainer variant="content">
        <VStack space="lg">
          {/* Header */}
          <VStack space="xs">
            <Text
              allowFontScaling={false}
              accessibilityRole="header"
              style={{ fontSize: FS_LG * fontScale, color: theme.text, fontWeight: 'bold', ...ffs }}
            >
              {title}
            </Text>
            {lesson?.estimatedMins && (
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}
              >
                ~{lesson.estimatedMins} min · {lesson.level} · {lesson.category}
              </Text>
            )}
          </VStack>

          {/* Deprecation banner */}
          {lesson?.deprecated?.replacedBy?.slug && (
            <TouchableOpacity
              testID="lesson-button-deprecated-banner"
              accessibilityLabel="View updated lesson"
              accessibilityRole="link"
              onPress={() => {
                const replacementSlug = lesson.deprecated!.replacedBy!.slug!;
                if (onNavigateToLesson) {
                  onNavigateToLesson(replacementSlug);
                } else {
                  navigation?.push?.('LessonDetail', { slug: replacementSlug });
                }
              }}
            >
              <Box
                style={{ backgroundColor: theme.isDark ? '#451A03' : '#FEF3C7', borderColor: '#D97706' }}
                borderWidth={1}
                p="$3"
                rounded="$lg"
              >
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_SM * fontScale, color: '#92400E', fontWeight: '600', ...ffs }}
                >
                  {t('lessons.detail.lessonUpdated')}
                </Text>
              </Box>
            </TouchableOpacity>
          )}

          {/* Content sections */}
          {content.sections.map((section: LessonSection, idx: number) => (
            <SectionRenderer
              key={idx}
              section={section}
              sectionIndex={idx}
              lessonSlug={slug || ''}
              lessonLanguage={lessonLanguage}
              sourceLocale={sourceLocale}
              targetLocale={targetLocale}
              voiceEnabled={voiceExercisesEnabled}
              exerciseProgress={exerciseProgress}
              onExerciseComplete={handleExerciseComplete}
              feedbackEnabled={lessonFeedbackEnabled}
              lessonId={lesson?.id || ''}
            />
          ))}

          {/* Whole-lesson feedback */}
          {lessonFeedbackEnabled && lesson?.id && (
            <FeedbackButton lessonId={lesson.id} locale={getActiveLocale(lessonLanguage, sourceLocale, targetLocale)} />
          )}

          {/* Listen on the go — hidden when the lesson has no linked audio
              episode, and while Audio Lessons is shelved (the flag guard also
              covers a stale cached slug from a previous flag-on run) */}
          {!isDraft && slug && AUDIO_LESSONS_ENABLED && linkedEpisodeSlug && (
            <TouchableOpacity
              testID="lesson-button-listen-on-the-go"
              accessibilityLabel={t('audioLessons.listenOnTheGo')}
              accessibilityRole="button"
              onPress={() => navigation?.push?.('AudioPlayer', { slug: linkedEpisodeSlug })}
              activeOpacity={0.7}
            >
              <Box
                py="$4"
                rounded="$xl"
                alignItems="center"
                style={{ backgroundColor: theme.card, borderWidth: 1, borderColor: theme.border }}
              >
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_MD * fontScale, fontWeight: '700', color: theme.text, ...ffs }}
                >
                  {t('audioLessons.listenOnTheGo')}
                </Text>
              </Box>
            </TouchableOpacity>
          )}

          {/* Completion failure — the card is withheld until a submit
              succeeds, so the retry has to live here (LSC-007) */}
          {completionError && (
            <ErrorState
              testID="lesson-completion-error"
              message={completionError}
              retryLabel={t('common.retry')}
              onRetry={handleComplete}
            />
          )}

          {/* Mark as complete button */}
          {!isDraft && slug && (
            <TouchableOpacity
              testID="lesson-complete-button"
              accessibilityLabel={t('lessons.detail.completeLesson')}
              accessibilityRole="button"
              onPress={handleComplete}
              disabled={isSubmittingCompletion}
              activeOpacity={0.7}
            >
              <Box
                py="$4"
                rounded="$xl"
                alignItems="center"
                style={{ backgroundColor: colors.primary }}
              >
                {isSubmittingCompletion ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text
                    allowFontScaling={false}
                    style={{ fontSize: FS_MD * fontScale, fontWeight: '700', color: '#FFFFFF', ...ffs }}
                  >
                    {t('lessons.detail.completeLesson')}
                  </Text>
                )}
              </Box>
            </TouchableOpacity>
          )}
        </VStack>
        </WebContainer>
      </ScrollView>
    </View>
  );
}

// ==============================
// Section Renderer
// ==============================

const SECTION_ICONS: Record<string, string> = {
  explanation: '💡',
  tip: '⚡',
  practice: '✏️',
  practice_ai: '🤖',
  summary: '📌',
};

const SectionRenderer = React.memo(function SectionRenderer({
  section,
  sectionIndex,
  lessonSlug,
  lessonLanguage,
  sourceLocale,
  targetLocale,
  voiceEnabled,
  exerciseProgress,
  onExerciseComplete,
  feedbackEnabled,
  lessonId,
}: {
  section: LessonSection;
  sectionIndex: number;
  lessonSlug: string;
  lessonLanguage: LessonLanguage;
  sourceLocale: SourceLocale;
  targetLocale: TargetLocale;
  voiceEnabled: boolean;
  exerciseProgress: Record<string, ExerciseProgress>;
  onExerciseComplete: (id: string, type: string, correct: boolean, score: number, extras?: Partial<ExerciseProgress>) => void;
  feedbackEnabled: boolean;
  lessonId: string;
}) {
  const { theme, fontScale, ffs } = useDisplayTheme();

  const sectionColors: Record<string, { bg: string; accent: string }> = {
    explanation: { bg: theme.isDark ? '#1E3A5F' : '#EFF6FF', accent: theme.primary },
    tip: { bg: theme.isDark ? '#451A03' : '#FEF3C7', accent: '#D97706' },
    practice: { bg: theme.isDark ? '#064E3B' : '#ECFDF5', accent: '#059669' },
    practice_ai: { bg: theme.isDark ? '#312E81' : '#EEF2FF', accent: '#6366F1' },
    summary: { bg: theme.primarySoft, accent: theme.primary },
  };

  const sc = sectionColors[section.type] || sectionColors.explanation;
  const icon = SECTION_ICONS[section.type] || '📄';

  const resolvedTitle = section.title
    ? resolveBilingualContent(section.title, lessonLanguage, sourceLocale, targetLocale)
    : null;

  return (
    <Box
      style={{ backgroundColor: theme.card, borderColor: theme.border }}
      borderWidth={1}
      rounded="$lg"
      overflow="hidden"
    >
      {/* Section header */}
      {resolvedTitle && (
        <Box style={{ backgroundColor: sc.bg }} px="$4" py="$3">
          <HStack space="sm" alignItems="center">
            <Text allowFontScaling={false} accessible={false} importantForAccessibility="no" style={{ fontSize: FS_MD * fontScale }}>
              {icon}
            </Text>
            <Text
              allowFontScaling={false}
              accessibilityRole="header"
              style={{ fontSize: FS_MD * fontScale, color: sc.accent, fontWeight: 'bold', ...ffs }}
            >
              {resolvedTitle}
            </Text>
          </HStack>
        </Box>
      )}

      {/* Section body */}
      <Box px="$4" py="$3">
        <VStack space="md">
          {section.body && (
            <BilingualText
              content={section.body}
              style={{ lineHeight: Math.ceil(22 * fontScale) }}
            />
          )}

          {/* AI-generated exercises */}
          {section.type === 'practice_ai' && section.aiAgentInstructions && (
            <AIExerciseLoader
              lessonSlug={lessonSlug}
              instructions={section.aiAgentInstructions}
            >
              {(exercises: GeneratedExercise[]) => (
                <VStack space="sm">
                  {exercises.map((ex, idx) => {
                    const exerciseId = `ai-${sectionIndex}-${idx}`;
                    return (
                      <ExerciseErrorBoundary key={exerciseId} exerciseId={exerciseId} exerciseType={ex.type}>
                        <ExerciseRenderer
                          exerciseId={exerciseId}
                          exercise={ex}
                          index={idx + 1}
                          voiceEnabled={voiceEnabled}
                          isCompleted={!!exerciseProgress[exerciseId]}
                          onComplete={onExerciseComplete}
                          lessonLanguage={lessonLanguage}
                          sourceLocale={sourceLocale}
                          targetLocale={targetLocale}
                        />
                      </ExerciseErrorBoundary>
                    );
                  })}
                </VStack>
              )}
            </AIExerciseLoader>
          )}

          {/* Regular exercises */}
          {section.exercises && section.exercises.length > 0 && (
            <VStack space="sm">
              {section.exercises.map((exercise, idx) => {
                const exerciseId = `${sectionIndex}-${idx}`;
                return (
                  <ExerciseErrorBoundary key={exerciseId} exerciseId={exerciseId} exerciseType={exercise.type}>
                    <ExerciseRenderer
                      exerciseId={exerciseId}
                      exercise={exercise}
                      index={idx + 1}
                      voiceEnabled={voiceEnabled}
                      isCompleted={!!exerciseProgress[exerciseId]}
                      onComplete={onExerciseComplete}
                      lessonLanguage={lessonLanguage}
                      sourceLocale={sourceLocale}
                      targetLocale={targetLocale}
                    />
                  </ExerciseErrorBoundary>
                );
              })}
            </VStack>
          )}
        </VStack>
      </Box>

      {/* Per-section feedback */}
      {feedbackEnabled && lessonId ? (
        <FeedbackButton lessonId={lessonId} sectionIndex={sectionIndex} locale={getActiveLocale(lessonLanguage, sourceLocale, targetLocale)} />
      ) : null}
    </Box>
  );
});

// ==============================
// Exercise Renderer (all 9 types)
// ==============================

const ExerciseRenderer = React.memo(function ExerciseRenderer({
  exerciseId,
  exercise,
  index,
  voiceEnabled,
  isCompleted,
  onComplete,
  lessonLanguage,
  sourceLocale,
  targetLocale,
}: {
  exerciseId: string;
  exercise: LessonExerciseContent | GeneratedExercise;
  index: number;
  voiceEnabled: boolean;
  isCompleted: boolean;
  onComplete: (id: string, type: string, correct: boolean, score: number, extras?: Partial<ExerciseProgress>) => void;
  lessonLanguage: LessonLanguage;
  sourceLocale: SourceLocale;
  targetLocale: TargetLocale;
}) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const resolvedPrompt = resolveBilingualContent(exercise.prompt, lessonLanguage, sourceLocale, targetLocale);

  const resolvedHint = exercise.hint
    ? resolveBilingualContent(exercise.hint, lessonLanguage, sourceLocale, targetLocale)
    : undefined;

  // AI-generated exercises may return bilingual objects for fields typed as string.
  // Safely resolve any field that might be string | BilingualContent.
  const resolveField = (value: unknown): string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    if (isBilingualContent(value)) {
      return resolveBilingualContent(value, lessonLanguage, sourceLocale, targetLocale);
    }
    return String(value);
  };

  // Coerce an array that might contain bilingual objects into string[]
  const resolveStringArray = (arr: unknown): string[] => {
    if (!Array.isArray(arr)) return [];
    return arr.map((item) => resolveField(item) ?? String(item));
  };

  // Validate required fields per exercise type.
  // Every type needs a non-empty prompt. Type-specific fields checked below.
  const ex = exercise as LessonExerciseContent;
  const requiredFields: Partial<Record<string, () => boolean>> = {
    fill_blank: () => !!ex.correctAnswer,
    choose_correct: () => !!ex.correctAnswer && Array.isArray(ex.options) && ex.options.length > 0,
    translate: () => !!ex.correctAnswer,
    reorder_words: () => Array.isArray(ex.words) && ex.words.length > 0 && Array.isArray(ex.correctOrder),
    match_pairs: () => Array.isArray(ex.pairs) && ex.pairs.length > 0,
    pronunciation_challenge: () => !!ex.targetWord,
    listen_and_choose: () => !!ex.audioText && Array.isArray(ex.options) && !!ex.correctAnswer,
    dictation: () => !!ex.audioText,
    error_correction: () => !!ex.sentence && Array.isArray(ex.errors),
    dialogue_completion: () => Array.isArray(ex.dialogue) && Array.isArray(ex.correctAnswers),
    listen_and_respond: () => !!ex.passage && Array.isArray(ex.questions),
  };

  const typeCheck = requiredFields[exercise.type];
  if (!resolvedPrompt || (typeCheck && !typeCheck())) {
    return (
      <Box p="$3" rounded="$md" style={{ backgroundColor: theme.isDark ? '#374151' : '#F3F4F6' }}>
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, textAlign: 'center', ...ffs }}
        >
          {t('lessons.detail.exerciseSkipped')}
        </Text>
      </Box>
    );
  }

  switch (exercise.type) {
    case 'fill_blank':
    case 'choose_correct':
    case 'translate':
      return (
        <FillOrChooseExercise
          exerciseId={exerciseId}
          type={exercise.type}
          prompt={resolvedPrompt}
          correctAnswer={resolveField(exercise.correctAnswer) || ''}
          acceptableAnswers={exercise.acceptableAnswers ? resolveStringArray(exercise.acceptableAnswers) : undefined}
          options={exercise.options ? resolveStringArray(exercise.options) : undefined}
          hint={resolvedHint}
          index={index}
          isCompleted={isCompleted}
          onComplete={onComplete}
        />
      );

    case 'reorder_words':
      return (
        <ReorderWordsExercise
          prompt={resolvedPrompt}
          words={resolveStringArray((exercise as LessonExerciseContent).words)}
          correctOrder={resolveStringArray((exercise as LessonExerciseContent).correctOrder)}
          hint={resolvedHint}
          onComplete={(correct, userOrder) =>
            onComplete(exerciseId, 'reorder_words', correct, correct ? 100 : 0, { userOrder })
          }
        />
      );

    case 'match_pairs':
      return (
        <MatchPairsExercise
          prompt={resolvedPrompt}
          pairs={(exercise as LessonExerciseContent).pairs || []}
          hint={resolvedHint}
          onComplete={(correct, matchedCount) =>
            onComplete(exerciseId, 'match_pairs', correct, correct ? 100 : 0, { matchedPairs: matchedCount })
          }
        />
      );

    case 'repeat_after':
      return (
        <RepeatAfterExercise
          prompt={resolvedPrompt}
          targetPhrase={resolveField((exercise as LessonExerciseContent).targetPhrase) || resolvedPrompt}
          phoneticGuide={resolveField((exercise as LessonExerciseContent).phoneticGuide)}
          hint={resolvedHint}
          voiceEnabled={voiceEnabled && ((exercise as LessonExerciseContent).voiceEnabled !== false)}
          onComplete={(correct, score) =>
            onComplete(exerciseId, 'repeat_after', correct, score, { voiceUsed: voiceEnabled })
          }
        />
      );

    case 'speak':
      return (
        <SpeakExercise
          prompt={resolvedPrompt}
          expectedKeywords={resolveStringArray((exercise as LessonExerciseContent).expectedKeywords)}
          hint={resolvedHint}
          voiceEnabled={voiceEnabled && ((exercise as LessonExerciseContent).voiceEnabled !== false)}
          onComplete={(correct, score) =>
            onComplete(exerciseId, 'speak', correct, score, { voiceUsed: voiceEnabled })
          }
        />
      );

    case 'pronunciation_challenge': {
      const resolvedCommonMistake = (exercise as LessonExerciseContent).commonMistake
        ? resolveBilingualContent((exercise as LessonExerciseContent).commonMistake!, lessonLanguage, sourceLocale, targetLocale)
        : undefined;
      return (
        <PronunciationChallengeExercise
          prompt={resolvedPrompt}
          targetWord={resolveField((exercise as LessonExerciseContent).targetWord) || ''}
          phoneticGuide={resolveField((exercise as LessonExerciseContent).phoneticGuide)}
          commonMistake={resolvedCommonMistake}
          hint={resolvedHint}
          voiceEnabled={voiceEnabled}
          minScore={(exercise as LessonExerciseContent).minScore}
          onComplete={(correct, score) =>
            onComplete(exerciseId, 'pronunciation_challenge', correct, score, { voiceUsed: voiceEnabled })
          }
        />
      );
    }

    case 'listen_and_choose':
      return (
        <ListenAndChooseExercise
          prompt={resolvedPrompt}
          audioText={resolveField((exercise as LessonExerciseContent).audioText) || ''}
          options={resolveStringArray(exercise.options)}
          correctAnswer={resolveField(exercise.correctAnswer) || ''}
          hint={resolvedHint}
          onComplete={(correct, score) =>
            onComplete(exerciseId, 'listen_and_choose', correct, score)
          }
        />
      );

    case 'dictation':
      return (
        <DictationExercise
          prompt={resolvedPrompt}
          audioText={resolveField((exercise as LessonExerciseContent).audioText) || ''}
          acceptableVariants={resolveStringArray((exercise as LessonExerciseContent).acceptableAnswers)}
          hint={resolvedHint}
          wordCount={(exercise as LessonExerciseContent).wordCount}
          speed={(exercise as LessonExerciseContent).speed}
          onComplete={(correct, score) =>
            onComplete(exerciseId, 'dictation', correct, score)
          }
        />
      );

    case 'error_correction':
      return (
        <ErrorCorrectionExercise
          prompt={resolvedPrompt}
          sentence={resolveField((exercise as LessonExerciseContent).sentence) || ''}
          errors={(exercise as LessonExerciseContent).errors || []}
          explanation={resolveField((exercise as LessonExerciseContent).explanation)}
          hint={resolvedHint}
          onComplete={(correct, score) =>
            onComplete(exerciseId, 'error_correction', correct, score)
          }
        />
      );

    case 'dialogue_completion':
      return (
        <DialogueCompletionExercise
          prompt={resolvedPrompt}
          dialogue={(exercise as LessonExerciseContent).dialogue || []}
          correctAnswers={resolveStringArray((exercise as LessonExerciseContent).correctAnswers)}
          options={exercise.options ? resolveStringArray(exercise.options) : undefined}
          hint={resolvedHint}
          context={resolveField((exercise as LessonExerciseContent).context)}
          onComplete={(correct, score) =>
            onComplete(exerciseId, 'dialogue_completion', correct, score)
          }
        />
      );

    case 'free_write':
      return (
        <FreeWriteExercise
          prompt={resolvedPrompt}
          writingType={(exercise as LessonExerciseContent).writingType || 'free response'}
          minWords={(exercise as LessonExerciseContent).minWords || 10}
          maxWords={(exercise as LessonExerciseContent).maxWords || 100}
          sampleAnswer={resolveField((exercise as LessonExerciseContent).sampleAnswer)}
          scaffolding={resolveStringArray((exercise as LessonExerciseContent).scaffolding)}
          targetVocabulary={resolveStringArray((exercise as LessonExerciseContent).targetVocabulary)}
          hint={resolvedHint}
          onComplete={(correct, score) =>
            onComplete(exerciseId, 'free_write', correct, score)
          }
        />
      );

    case 'listen_and_respond':
      return (
        <ListenAndRespondExercise
          prompt={resolvedPrompt}
          passage={resolveField((exercise as LessonExerciseContent).passage) || ''}
          passageTitle={resolveField((exercise as LessonExerciseContent).passageTitle)}
          questions={((exercise as LessonExerciseContent).questions || []).map((q) => ({ ...q, question: resolveField(q.question) || '', explanation: resolveField(q.explanation) }))}
          showTranscript={(exercise as LessonExerciseContent).showTranscript}
          speed={(exercise as LessonExerciseContent).speed}
          hint={resolvedHint}
          onComplete={(correct, score) =>
            onComplete(exerciseId, 'listen_and_respond', correct, score)
          }
        />
      );

    default:
      return (
        <Box p="$3" rounded="$md" style={{ backgroundColor: theme.isDark ? '#374151' : '#F3F4F6' }}>
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}
          >
            {t('lessons.detail.exerciseNotSupported', { type: exercise.type })}
          </Text>
        </Box>
      );
  }
});

// FillOrChooseExercise imported from ../components/exercises
